import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  CreateGitCredentialBody,
  GitCredentialResponse,
  GitCredentialTestResponse,
  UpdateGitCredentialBody,
} from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

type Status = 'idle' | 'loading' | 'success' | 'error';

/**
 * The git credential registry — owner-root scoped, so there is no tenant
 * argument here the way there is for git bindings. The server already narrows
 * to the caller's owner root, which is why a picker built on this can never
 * offer a credential the server would then refuse.
 */
export function useGitCredentials({ enabled = true }: { enabled?: boolean } = {}) {
  const apiClient = useApiClient();
  const [credentials, setCredentials] = useState<GitCredentialResponse[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();
  const inFlight = useRef(false);

  const reload = useCallback(() => {
    if (!enabled || inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    apiClient
      .listGitCredentials()
      .then((res) => {
        setCredentials(res.data);
        setStatus('success');
        setError(undefined);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load git credentials');
        setStatus('error');
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [apiClient, enabled]);

  useEffect(() => {
    if (enabled) reload();
    else {
      setCredentials([]);
      setStatus('idle');
    }
  }, [enabled, reload]);

  const createCredential = useCallback(
    async (body: CreateGitCredentialBody) => {
      const created = await apiClient.createGitCredential(body);
      reload();
      return created;
    },
    [apiClient, reload],
  );

  const updateCredential = useCallback(
    async (id: string, body: UpdateGitCredentialBody) => {
      const updated = await apiClient.updateGitCredential(id, body);
      reload();
      return updated;
    },
    [apiClient, reload],
  );

  const deleteCredential = useCallback(
    async (id: string) => {
      await apiClient.deleteGitCredential(id);
      reload();
    },
    [apiClient, reload],
  );

  // Deliberately does NOT reload: a probe is a read, and re-fetching the list
  // under the operator while they are reading a result is disorienting.
  const testCredential = useCallback(
    async (id: string, repoUrl?: string): Promise<GitCredentialTestResponse> =>
      apiClient.testGitCredential(id, repoUrl),
    [apiClient],
  );

  return {
    credentials,
    status,
    error,
    reload,
    createCredential,
    updateCredential,
    deleteCredential,
    testCredential,
  };
}
